import { isImageDataUrl } from './image-data-url';

export function stringInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  // Single-word names use only the first letter ("Bob" -> "B"); multi-word
  // names use the first letter of the first two words ("Bob Tomson" -> "BT").
  // Capped at two characters so avatars stay legible.
  if (words.length === 1) return words[0][0].toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

// Deterministic hue in [0, 360) derived from the name. Because it's a pure
// function of the name, every client computes the same color for the same user
// without any coordination or data transfer.
export function nameToHue(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  return ((hash % 360) + 360) % 360;
}

function escapeXml(value: string): string {
  return value.replace(
    /[<>&'"]/g,
    (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', "'": '&apos;', '"': '&quot;' })[c]!
  );
}

// Builds a self-contained SVG avatar (colored background + initials) as a
// `data:` URI. Pure function of `name` — no network request, no server. This is
// the default avatar used whenever a user has not set a custom one. The SVG is
// rendered inside an <img>, so it can only use system fonts (page web-fonts are
// not available in that context).
export function getAvatar(name: string): string {
  const trimmed = name.trim();
  const initials = escapeXml(stringInitials(trimmed));
  const background = trimmed ? `hsl(${nameToHue(trimmed)}, 60%, 45%)` : 'hsl(0, 0%, 60%)';
  // Small gap between the two initials. Value is in SVG user units (the box is
  // 100x100), so 4 ≈ 0.1rem at the default --avatar-width (2.5rem) and scales
  // with the avatar everywhere else. Only applied for two-letter initials;
  // `text-anchor="middle"` keeps the pair centered (no trailing letter-space is
  // added to the advance), so single letters / "?" need no special handling.
  const letterSpacing = initials.length > 1 ? ' letter-spacing="4"' : '';
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100">` +
    `<rect width="100" height="100" fill="${background}"/>` +
    `<text x="50" y="50" dy=".35em" text-anchor="middle"${letterSpacing} ` +
    `font-family="system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif" ` +
    `font-size="42" font-weight="600" fill="#fff">${initials}</text>` +
    `</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

// Resolves which avatar to display for a user: their custom avatar when set
// (synced across clients via userData), otherwise a locally generated default
// derived from their name. A custom avatar is always an inline image data URL
// (see `cropToAvatarDataUrl`); anything else — a value edited into local
// storage, sent by a modified client, or persisted before receivers validated
// it — falls back to the default instead of being loaded.
export function resolveAvatar(avatar: string | undefined, name: string): string {
  return isImageDataUrl(avatar) ? avatar : getAvatar(name);
}
